/**
 * Вкладка «Сроки и напоминания» раздела «Экология» панели администратора (ecology-catalog, макет
 * 16.09.2026). Две карточки со своими кнопками «Сохранить»:
 *   — «Светофор и ежегодный вопрос»: пороги «истекает скоро» — для документов и ежегодной отчётности
 *     (90 дней), ежеквартальной (30) и ежемесячной (7) отчётности, ответ заказчика 17.09 — и через
 *     сколько месяцев после опроса кабинет спрашивает клиента «что менялось». Значения раздела
 *     «Экология»; настройки обучения не меняются;
 *   — «Календарь отчётности»: даты сдачи ежегодной отчётности из справочника (ДД.ММ), по которым
 *     кабинет ставит отчётам «ближайший срок». Это те же даты, что в модалке позиции справочника.
 *   — «Письма о сроках» (ecology-reminders): за сколько дней до срока кабинет пишет клиенту и
 *     состояние двух видов писем экологии. Тексты писем и включение видов — в «Уведомлениях»:
 *     офсеты здесь правятся тем же PATCH шаблона, что и там.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { adminApi, type AdminTemplate, type EcoCatalogItem, type EcoSettings } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { formatEcoDeadline, parseEcoDeadline } from '@/lib/ecology'
import { AppLink } from '@/app/navigation'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'

/** Короткое подтверждение сохранения, гаснет через 2,5 с (`.saved` макета). */
function useSavedFlag(): [boolean, (v: boolean) => void] {
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    if (!saved) return
    const timer = window.setTimeout(() => setSaved(false), 2500)
    return () => window.clearTimeout(timer)
  }, [saved])
  return [saved, setSaved]
}

function SaveBar({
  saving,
  saved,
  disabled,
}: {
  saving: boolean
  saved: boolean
  disabled?: boolean
}) {
  return (
    <div className="mt-4 flex items-center gap-3">
      <Button type="submit" size="sm" disabled={saving || disabled}>
        {saving ? 'Сохранение…' : 'Сохранить'}
      </Button>
      {saved ? (
        <span role="status" className="text-13 font-medium text-green">
          Сохранено
        </span>
      ) : null}
    </div>
  )
}

function parseBounded(value: string, max: number): number | null {
  const n = Number(value)
  return value.trim() && Number.isInteger(n) && n >= 1 && n <= max ? n : null
}

/** Пороги «истекает скоро»: ключ, подпись и верхняя граница (дней; нижняя — 1). */
const THRESHOLD_FIELDS: { key: keyof EcoSettings; label: string }[] = [
  { key: 'expiringDays', label: 'Документы и ежегодная отчётность' },
  { key: 'expiringDaysQuarterly', label: 'Ежеквартальная отчётность' },
  { key: 'expiringDaysMonthly', label: 'Ежемесячная отчётность' },
]

/** Все поля карточки с границами — для проверки перед сохранением. */
const SETTINGS_LIMITS: Record<keyof EcoSettings, number> = {
  expiringDays: 365,
  expiringDaysQuarterly: 365,
  expiringDaysMonthly: 365,
  changeCheckMonths: 60,
}

function settingsToValues(s: EcoSettings): Record<keyof EcoSettings, string> {
  return {
    expiringDays: String(s.expiringDays),
    expiringDaysQuarterly: String(s.expiringDaysQuarterly),
    expiringDaysMonthly: String(s.expiringDaysMonthly),
    changeCheckMonths: String(s.changeCheckMonths),
  }
}

function SettingsCard({
  settings,
  onSettingsChange,
}: {
  settings: EcoSettings
  onSettingsChange: (s: EcoSettings) => void
}) {
  const [values, setValues] = useState(() => settingsToValues(settings))
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useSavedFlag()
  const [error, setError] = useState<string | null>(null)

  const keys = Object.keys(SETTINGS_LIMITS) as (keyof EcoSettings)[]
  const parsed = Object.fromEntries(
    keys.map((k) => [k, parseBounded(values[k], SETTINGS_LIMITS[k])]),
  ) as Record<keyof EcoSettings, number | null>
  const invalid = keys.some((k) => parsed[k] === null)

  function errorOf(key: keyof EcoSettings): string | undefined {
    return showErrors && parsed[key] === null
      ? `Целое число от 1 до ${SETTINGS_LIMITS[key]}`
      : undefined
  }

  function change(key: keyof EcoSettings, value: string) {
    setSaved(false)
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (invalid) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const updated = await adminApi.ecology.updateSettings({
        expiringDays: parsed.expiringDays as number,
        expiringDaysQuarterly: parsed.expiringDaysQuarterly as number,
        expiringDaysMonthly: parsed.expiringDaysMonthly as number,
        changeCheckMonths: parsed.changeCheckMonths as number,
      })
      onSettingsChange(updated)
      setValues(settingsToValues(updated))
      setShowErrors(false)
      setSaved(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить настройки'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card header="Светофор и ежегодный вопрос">
      <form onSubmit={submit} noValidate>
        <p className="text-12 text-txt-tertiary">
          Значения раздела «Экология» для всех клиентов. Настройки обучения здесь не меняются.
          Журнал движения отходов ведётся постоянно — сроков у него нет.
        </p>
        {error ? (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        ) : null}
        <p className="mb-1.5 mt-4 text-13 font-medium text-txt">
          «Истекает скоро» — за сколько дней до срока
        </p>
        <div className="grid grid-cols-1 gap-3 nw:grid-cols-3 nw:items-end">
          {THRESHOLD_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} required error={errorOf(f.key)} className="my-0">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  max={SETTINGS_LIMITS[f.key]}
                  value={values[f.key]}
                  invalid={Boolean(errorOf(f.key))}
                  onChange={(e) => change(f.key, e.target.value)}
                />
              )}
            </Field>
          ))}
        </div>
        <Field
          label="Ежегодный вопрос «что менялось» — через сколько месяцев после опроса"
          required
          error={errorOf('changeCheckMonths')}
          className="mb-0"
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={1}
              max={SETTINGS_LIMITS.changeCheckMonths}
              className="max-w-32"
              value={values.changeCheckMonths}
              invalid={Boolean(errorOf('changeCheckMonths'))}
              onChange={(e) => change('changeCheckMonths', e.target.value)}
            />
          )}
        </Field>
        <SaveBar saving={saving} saved={saved} />
      </form>
    </Card>
  )
}

function CalendarCard({
  items,
  onItemsChange,
}: {
  items: EcoCatalogItem[]
  onItemsChange: (items: EcoCatalogItem[]) => void
}) {
  const reports = useMemo(
    () => items.filter((i) => i.kind === 'report' && i.period === 'yearly'),
    [items],
  )
  // Введённые даты по id позиции; без правки — дата из справочника.
  const [values, setValues] = useState<Record<string, string>>({})
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useSavedFlag()
  const [error, setError] = useState<string | null>(null)

  function valueOf(item: EcoCatalogItem): string {
    return values[item.id] ?? formatEcoDeadline(item.deadlineDay, item.deadlineMonth)
  }

  /**
   * Ошибка строки: введённая дата должна существовать. Пустое поле допустимо только у позиции без
   * даты — убрать дату можно в справочнике (тип и периодичность правятся там же).
   */
  function rowError(item: EcoCatalogItem): string | undefined {
    const value = valueOf(item).trim()
    if (!value)
      return item.deadlineDay === null ? undefined : 'Укажите дату — убрать её можно в справочнике'
    return parseEcoDeadline(value) ? undefined : 'Дата в формате ДД.ММ, например 25.03'
  }

  const hasErrors = reports.some((item) => rowError(item) !== undefined)

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (hasErrors) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const rows = reports.flatMap((item) => {
        const parsed = parseEcoDeadline(valueOf(item))
        return parsed ? [{ id: item.id, deadlineDay: parsed.day, deadlineMonth: parsed.month }] : []
      })
      const updated = await adminApi.ecology.saveCalendar(rows)
      setValues({})
      setShowErrors(false)
      onItemsChange(updated)
      setSaved(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить календарь'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card header="Календарь отчётности">
      <form onSubmit={submit} noValidate>
        <p className="text-12 text-txt-tertiary">
          Даты сдачи, по которым кабинет ставит «ближайший срок» отчётам. Список — ежегодная
          отчётность из справочника.
        </p>
        {error ? (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        ) : null}
        {reports.length === 0 ? (
          <p className="mt-3 text-txt-secondary">В справочнике нет ежегодной отчётности</p>
        ) : (
          <ul className="mt-2">
            {reports.map((item) => {
              const err = showErrors ? rowError(item) : undefined
              return (
                <li key={item.id} className="border-b border-line py-2.5 last:border-b-0">
                  <Field label={item.name} error={err} className="my-0">
                    {(props) => (
                      <Input
                        {...props}
                        className="max-w-32"
                        placeholder="ДД.ММ"
                        inputMode="numeric"
                        value={valueOf(item)}
                        invalid={Boolean(err)}
                        onChange={(e) => {
                          setSaved(false)
                          setValues((prev) => ({ ...prev, [item.id]: e.target.value }))
                        }}
                      />
                    )}
                  </Field>
                </li>
              )
            })}
          </ul>
        )}
        <SaveBar saving={saving} saved={saved} disabled={reports.length === 0} />
      </form>
    </Card>
  )
}

/** Виды писем экологии: ключ и подпись (ярлык приходит с сервера, здесь — запасной). */
const ECO_MAIL_KINDS: { kind: string; label: string }[] = [
  { kind: 'eco_reminder', label: 'Экология: приближается срок' },
  { kind: 'eco_overdue', label: 'Экология: срок истёк' },
]

/** «60, 30, 7» из офсетов шаблона. */
function offsetsToValue(offsets: number[]): string {
  return offsets.join(', ')
}

/** Дни из строки «60, 30, 7»; пусто или мусор — null (показываем ошибку). */
function parseOffsets(value: string): number[] | null {
  const parts = value
    .split(/[,;\s]+/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 0) return null
  const days: number[] = []
  for (const p of parts) {
    const n = Number(p)
    if (!Number.isInteger(n) || n < 1 || n > 365 || days.includes(n)) return null
    days.push(n)
  }
  return days.sort((a, b) => b - a)
}

function MailsCard() {
  const [templates, setTemplates] = useState<AdminTemplate[] | null>(null)
  const [value, setValue] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useSavedFlag()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    adminApi.notifications
      .listTemplates(controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return
        setTemplates(list)
        const reminder = list.find((t) => t.kind === 'eco_reminder')
        setValue(offsetsToValue(reminder?.offsets ?? []))
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить письма'))
      })
    return () => controller.abort()
  }, [])

  const parsed = parseOffsets(value)
  const fieldError = showErrors && parsed === null ? 'Дни через запятую, например 60, 30, 7' : undefined

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (saving) return
    if (parsed === null) {
      setShowErrors(true)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const updated = await adminApi.notifications.patchTemplate('eco_reminder', { offsets: parsed })
      setTemplates((prev) => (prev ?? []).map((t) => (t.kind === updated.kind ? updated : t)))
      setValue(offsetsToValue(updated.offsets))
      setShowErrors(false)
      setSaved(true)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить расписание'))
    } finally {
      setSaving(false)
    }
  }

  const reminder = templates?.find((t) => t.kind === 'eco_reminder')
  return (
    <Card header="Письма о сроках">
      <form onSubmit={submit} noValidate>
        <p className="text-12 text-txt-tertiary">
          Кабинет пишет клиенту о сроках документов и отчётности по экологии: по расписанию до срока
          и один раз, когда срок истёк. Пока по строке идёт заказ у Центрики, письма не уходят.
        </p>
        {error ? (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        ) : null}
        <Field
          label="Напоминать за (дней до срока)"
          required
          error={fieldError}
          hint="Через запятую; по умолчанию 60, 30 и 7"
        >
          {(props) => (
            <Input
              {...props}
              className="max-w-48"
              inputMode="numeric"
              placeholder="60, 30, 7"
              value={value}
              disabled={!reminder}
              invalid={Boolean(fieldError)}
              onChange={(e) => {
                setSaved(false)
                setValue(e.target.value)
              }}
            />
          )}
        </Field>
        <ul className="mb-1 mt-3 flex flex-col gap-1.5">
          {ECO_MAIL_KINDS.map((m) => {
            const t = templates?.find((x) => x.kind === m.kind)
            return (
              <li key={m.kind} className="flex flex-wrap items-center gap-2 text-13 text-txt">
                {t?.label ?? m.label}
                {templates ? (
                  <Badge variant={t?.enabled ? 'green' : 'gray'}>
                    {t ? (t.enabled ? 'включено' : 'выключено') : 'нет в списке писем'}
                  </Badge>
                ) : null}
              </li>
            )
          })}
        </ul>
        <p className="text-12 text-txt-tertiary">
          Тексты писем и включение видов — в разделе{' '}
          <AppLink to="/team/notifications">«Уведомления»</AppLink>.
        </p>
        <SaveBar saving={saving} saved={saved} disabled={!reminder} />
      </form>
    </Card>
  )
}

export function TermsTab({
  items,
  settings,
  onItemsChange,
  onSettingsChange,
}: {
  items: EcoCatalogItem[]
  settings: EcoSettings
  onItemsChange: (items: EcoCatalogItem[]) => void
  onSettingsChange: (s: EcoSettings) => void
}) {
  return (
    <Grid cols={2}>
      <SettingsCard settings={settings} onSettingsChange={onSettingsChange} />
      <CalendarCard items={items} onItemsChange={onItemsChange} />
      <MailsCard />
    </Grid>
  )
}
